'use client';

import { useState, useEffect } from 'react';
import { Link2, Search, X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { useHashNavigation } from '@/hooks/use-hash-navigation';
import { cn } from '@/lib/utils';
import type { FAQItem } from '../utils/parse-docx';
import { DOC_CONTENT_CLASSES } from '../utils/doc-content-classes';

interface FAQAccordionProps {
  faqs: FAQItem[];
  children?: React.ReactNode | ((searchQuery: string) => React.ReactNode);
}

/** Shared by the visible list and the hash handler, so the two cannot disagree
 *  about whether a given FAQ is currently on screen. */
function matchesQuery(faq: FAQItem, searchQuery: string): boolean {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return true;
  return (
    faq.title.toLowerCase().includes(query) ||
    faq.content.toLowerCase().includes(query)
  );
}

export function FAQAccordion({ faqs, children }: FAQAccordionProps) {
  const { hash, setHash, scrollToHash } = useHashNavigation({ scrollOffset: 120 });
  const [expandedValue, setExpandedValue] = useState<string>('');
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredFaqs = faqs.filter((faq) => matchesQuery(faq, searchQuery));

  // Handles both the initial page load with a hash and the cross-links inside
  // an answer's body.
  //
  // Those cross-links point at other FAQs, which an active search may have
  // filtered out of the list. The target then never renders, so expanding it
  // puts nothing on screen and there is no element left to scroll to — the
  // click looks like it did nothing at all. Dropping the query first brings
  // the target back into the list, keeping the jump in-page rather than
  // sending the reader off to a separate view.
  useEffect(() => {
    if (!hash) return;
    const faq = faqs.find((f) => f.slug === hash);
    if (!faq) return;

    // Functional form: this must not re-run on every keystroke, so the effect
    // reads the current query here instead of taking it as a dependency.
    setSearchQuery((query) => (matchesQuery(faq, query) ? query : ''));

    setExpandedValue(faq.slug);
    // Small delay to ensure accordion is rendered
    setTimeout(() => {
      scrollToHash(hash);
    }, 100);
  }, [hash, faqs, scrollToHash]);

  // Handle accordion value change
  const handleValueChange = (value: string) => {
    setExpandedValue(value);
    if (value) {
      setHash(value);
    } else {
      // Clear hash when closing
      setHash('');
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Input */}
      <div className="relative w-full">
        <label htmlFor="search-faqs" className="sr-only">
          Search FAQs
        </label>
        <Input
          id="search-faqs"
          type="text"
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="placeholder:font-normal placeholder:text-[#948780]/80 placeholder:text-md text-lg h-12 pl-12 pr-10 rounded-full border border-[#cdc4c1] focus:border-gray-400 focus:outline-none w-full bg-transparent"
        />
        <Search
          aria-hidden="true"
          className="text-[#948780] pointer-events-none absolute top-1/2 left-5 h-4 w-4 -translate-y-1/2 select-none"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-[#948780] hover:text-gray-700 z-10 focus-visible:outline-2 focus-visible:outline-primary rounded-full p-1"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {typeof children === 'function' ? children(searchQuery) : children}

      <h2 className="font-merriweather mb-3 border-b-2 border-[#cdc4c1] pb-2 text-xl font-bold text-[#3D2817] md:text-2xl">
        FAQs
      </h2>

      {filteredFaqs.length === 0 ? (
        <div className="text-center py-12 text-[#6B5647] font-merriweather" role="status">
          <p className="text-lg font-semibold mb-2">No results found</p>
          <p className="text-sm opacity-85">
            We couldn't find any FAQs matching "{searchQuery}". Try using different keywords.
          </p>
        </div>
      ) : (
        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={expandedValue}
          onValueChange={handleValueChange}
        >
          {filteredFaqs.map((faq) => (
            <AccordionItem
              key={faq.slug}
              value={faq.slug}
              id={faq.slug}
              className="border-[#C9BDB0] group"
            >
              <div className="relative flex items-center">
                <AccordionTrigger className="text-[#3D2817] hover:text-[#6B5647] text-base font-semibold font-merriweather hover:no-underline flex-1 cursor-pointer">
                  <span className="text-left">{faq.title}</span>
                </AccordionTrigger>
                <button
                  type="button"
                  onClick={(e) => handleCopyLink(faq.slug, e)}
                  className={cn(
                    'absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-primary transition-opacity p-1 rounded hover:bg-[#E8E0D8] cursor-pointer',
                    copiedSlug === faq.slug && 'opacity-100'
                  )}
                  title={copiedSlug === faq.slug ? 'Copied!' : 'Copy link'}
                  aria-label={`Copy link to ${faq.title}`}
                >
                  <Link2
                    aria-hidden="true"
                    className={cn(
                      'w-4 h-4',
                      copiedSlug === faq.slug ? 'text-green-600' : 'text-[#6B5647]'
                    )}
                  />
                </button>
              </div>
              <AccordionContent className="text-[#6B5647] leading-relaxed font-merriweather">
                <div
                  className={DOC_CONTENT_CLASSES}
                  dangerouslySetInnerHTML={{ __html: faq.content }}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
